import { useState } from 'react';
import { Plus, Calendar, Clock, Users, CheckCircle, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { DataTable, Column } from '@/components/common/DataTable';
import { useToast } from '@/components/ui/Toast';
import { useLeaveApplications, useMyLeaveApplications, useApproveLeave, useLeaveBalances } from '@/hooks/useLeave';
import { LeaveApplication, LEAVE_APPLICATION_STATUSES, LEAVE_TYPES } from '@/types';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

const getStatusBadge = (status: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'destructive',
    cancelled: 'secondary',
  };
  const config = LEAVE_APPLICATION_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'default'}>{config?.label || status}</Badge>;
};

export function LeavePage() {
  const [activeTab, setActiveTab] = useState('my-leave');
  const { toast } = useToast();
  
  const { data: myLeaveData } = useMyLeaveApplications();
  const { data: teamLeaveData } = useLeaveApplications({ status: 'pending' });
  const { data: leaveBalances } = useLeaveBalances();
  const approveLeave = useApproveLeave();

  const myApplications = myLeaveData?.data || [];
  const teamApplications = teamLeaveData?.data || [];

  const handleApprove = async (id: string, approved: boolean) => {
    try {
      await approveLeave.mutateAsync({ id, approved });
      toast({
        title: 'Success',
        description: `Leave ${approved ? 'approved' : 'rejected'} successfully`,
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to process leave application',
        variant: 'destructive',
      });
    }
  };

  const leaveColumns: Column<LeaveApplication>[] = [
    {
      key: 'type',
      header: 'Type',
      cell: (row) => LEAVE_TYPES.find(t => t.value === row.leaveType)?.label || row.leaveType,
    },
    {
      key: 'dates',
      header: 'Dates',
      cell: (row) => (
        <span>
          {formatDate(row.startDate)} - {formatDate(row.endDate)}
        </span>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      cell: (row) => `${row.totalHours} hrs`,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => getStatusBadge(row.status),
    },
  ];

  const teamColumns: Column<LeaveApplication>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => <span className="font-medium">{row.employeeName}</span>,
    },
    ...leaveColumns,
    {
      key: 'actions',
      header: '',
      width: '150px',
      cell: (row) => row.status === 'pending' ? (
        <div className="flex gap-1">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleApprove(row.id, true)}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Approve
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            className="text-destructive"
            onClick={() => handleApprove(row.id, false)}
          >
            <XCircle className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </div>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave Management"
        description="Manage leave balances and applications"
        action={{
          label: 'Apply for Leave',
          onClick: () => window.location.href = '/payroll/leave/apply',
          icon: <Plus className="h-4 w-4" />,
        }}
      />

      {/* Leave Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {leaveBalances?.map((balance) => (
          <Card key={balance.type}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {LEAVE_TYPES.find(t => t.value === balance.type)?.label || balance.type}
                  </p>
                  <p className={cn(
                    "text-2xl font-bold",
                    balance.balance < 10 && "text-warning",
                    balance.balance <= 0 && "text-destructive"
                  )}>
                    {balance.balance} {balance.unit}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Entitlement: {balance.entitlement} {balance.unit}
              </p>
            </CardContent>
          </Card>
        ))}
        {!leaveBalances || leaveBalances.length === 0 && (
          <Card className="md:col-span-4">
            <CardContent className="p-4 text-center">
              <p className="text-muted-foreground">No leave balances available</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto">
          <TabsTrigger value="my-leave" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            My Leave
          </TabsTrigger>
          <TabsTrigger value="team-leave" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Leave
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-leave" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Leave Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={myApplications}
                columns={leaveColumns}
                keyExtractor={(row) => row.id}
                emptyMessage="No leave applications found. Apply for leave to get started."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="team-leave" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={teamApplications}
                columns={teamColumns}
                keyExtractor={(row) => row.id}
                emptyMessage="No pending leave applications to approve."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
